import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Input as AntInput, Modal, Spin } from 'antd';
import { useDebounce } from '../../../shared/hooks/useDebounce';
import { fetchUsers } from '../../users/api/usersApi';
import { startConversation } from '../api/chatApi';
import { PAGE_SIZE } from '../../../config';
import styles from '../chat.module.scss';

const patientLabel = (patient) => {
  const name = `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim();
  return name || patient?.phone || patient?.email || 'Nepoznat pacijent';
};

// Backend get-or-create moze vratiti Conversation direktno ili umotan.
const extractConversation = (data) => data?.conversation || data || null;

const NewMessageModal = ({ open, onClose, onConversationReady }) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const [patients, setPatients] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [creatingId, setCreatingId] = useState(null);
  const [error, setError] = useState('');

  const latestRequestRef = useRef(0);

  const loadPatients = useCallback(
    async (nextPage, { append } = { append: false }) => {
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;
      setListLoading(true);

      const { data, ok, message } = await fetchUsers({
        page: nextPage,
        limit: PAGE_SIZE,
        filter: {},
        search: debouncedSearch || null,
      });

      if (requestId !== latestRequestRef.current) return;
      setListLoading(false);

      if (!ok || !data) {
        setError(message || 'Neuspesno ucitavanje pacijenata.');
        return;
      }

      setError('');
      const users = Array.isArray(data.users) ? data.users : [];
      setPatients((prev) => (append ? [...prev, ...users] : users));
      setTotalPages(data?.pagination?.totalPages || 1);
      setPage(data?.pagination?.currentPage || nextPage);
    },
    [debouncedSearch]
  );

  // Reset + (re)load kada se modal otvori ili se promeni pretraga.
  useEffect(() => {
    if (!open) return;
    setCreatingId(null);
    loadPatients(1, { append: false });
  }, [open, loadPatients]);

  const handleSelectPatient = useCallback(
    async (patient) => {
      if (!patient?._id || creatingId) return;
      setCreatingId(patient._id);
      setError('');

      const { data, ok, message } = await startConversation(patient._id);

      if (!ok) {
        setError(message || 'Neuspesno pokretanje razgovora.');
        setCreatingId(null);
        return;
      }

      const conversation = extractConversation(data);
      if (!conversation?.id) {
        setError('Backend nije vratio validan razgovor.');
        setCreatingId(null);
        return;
      }

      setCreatingId(null);
      onConversationReady(conversation);
    },
    [creatingId, onConversationReady]
  );

  const handleCancel = () => {
    setSearch('');
    setPatients([]);
    setError('');
    setCreatingId(null);
    onClose();
  };

  const hasMore = page < totalPages;

  return (
    <Modal
      className="send_notification_modal"
      title="Nova poruka"
      open={open}
      onCancel={handleCancel}
      footer={null}
    >
      <AntInput
        allowClear
        placeholder="Pretraži pacijente"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      {error && (
        <Alert title={error} type="error" showIcon style={{ marginTop: 12 }} />
      )}

      <div className={styles.picker}>
        {listLoading && patients.length === 0 ? (
          <div className={styles.picker__empty}>
            <Spin size="small" />
          </div>
        ) : patients.length === 0 ? (
          <div className={styles.picker__empty}>Nema pacijenata.</div>
        ) : (
          patients.map((patient) => (
            <button
              key={patient._id}
              type="button"
              className={styles.picker__row}
              onClick={() => handleSelectPatient(patient)}
              disabled={!!creatingId}
            >
              <span className={styles.picker__name}>
                {patientLabel(patient)}
              </span>
              <span className={styles.picker__meta}>
                {creatingId === patient._id ? (
                  <Spin size="small" />
                ) : (
                  patient?.phone || ''
                )}
              </span>
            </button>
          ))
        )}
      </div>

      {hasMore && (
        <div className={styles.picker__more}>
          <Button
            disabled={listLoading || !!creatingId}
            onClick={() => loadPatients(page + 1, { append: true })}
          >
            Učitaj još
          </Button>
        </div>
      )}
    </Modal>
  );
};

NewMessageModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConversationReady: PropTypes.func.isRequired,
};

export default NewMessageModal;
